import React, {useEffect, useMemo, useState} from 'react';
import {ServiceEvent} from 'crqlar-react-common/dist/models/agenda/agenda.types';
import {useLocation, useParams} from 'react-router-dom';
import {
    Eventcalendar,
    localeDe,
    localeEn,
    MbscEventcalendarView
} from '@mobiscroll/react';
import '@mobiscroll/react/dist/css/mobiscroll.scss';
import {
    useAgendaEvents
} from 'crqlar-react-common/dist/utils/hooks/agenda/useAgendaEvents';
import {Alert, Box, CircularProgress, Snackbar, Typography} from '@mui/material';
import Footer from './Footer';
import AgendaEventDialog from './AgendaEventDialog';
import AgendaEventItem from './AgendaEventItem';

interface Props {
}

const AgendaView = ({}: Props) => {
    const {organizationName = 'Agenda', agendaId = ''} = useParams<{
        organizationName: string;
        agendaId: string;
    }>();

    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const dateParam = query.get("date");

    const [selected, setSelected] = useState<Date>(() => {
        if (dateParam) {
            const parsed = new Date(dateParam);
            if (!isNaN(parsed.getTime())) {
                return parsed;
            }
        }
        return new Date();
    });

    const [preview, setPreview] = useState<ServiceEvent | undefined>(undefined);
    const [showPastEventSnackbar, setShowPastEventSnackbar] = useState(false);

    const view: MbscEventcalendarView = useMemo(
        () => ({
            calendar: {type: 'week'},
            agenda: {type: 'day'}
        }),
        []
    );

    const {
        events,
        isLoading,
        ui,
        isError,
        agenda,
        refetch
    } = useAgendaEvents(agendaId, selected);

    useEffect(() => {
        if (isLoading) {
            document.title = 'Loading…';
            return;
        }

        if (ui?.locale?.['Title']) {
            document.title = `${ui.locale['Title']}`;
        } else {
            document.title = '';
        }
    }, [isLoading, ui]);

    const font = ui?.font ?? {
        mediaURI: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap',
        family: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif'
    };
    const css = ui?.css ?? {primary: '', onPrimary: '#ffffff', panelBackground: ''};
    const logo = ui?.logo ?? {mediaURI: null as string | null};
    const defaultLanguageCode = ui?.defaultLanguageCode || navigator.language?.split('-')[0] || 'en';
    const customCSS = ui?.customCSS ?? '';

    const mobiscrollLocale = useMemo(() => {
        const code = ui?.defaultLanguageCode?.toLowerCase();
        return code?.startsWith('de') ? localeDe : localeEn;
    }, [ui?.defaultLanguageCode]);

    // Centered spinner while loading
    if (isLoading) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <CircularProgress aria-label="Loading agenda"/>
            </Box>
        );
    }

    // Centered error element
    if (isError) {
        document.title = 'Error';
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Typography sx={{p: 2}}>Failed to load agenda</Typography>
            </Box>
        );
    }

    return (
        <>
            <style>{`
@import url('${font.mediaURI}');
:root {
  --agenda-primary: ${!css.primary.trim().length ? "#3c3c3b" : css.primary};
  --agenda-on-primary: ${css.onPrimary};
  --agenda-panel-bg: ${css.panelBackground};
  --font-family: ${font.family};
  --cancel-color: #999999;
  --accent: var(--agenda-primary);
}

html, body, #root {
  font-family: var(--font-family);
  margin: 0;
  padding: 0;
  min-height: 100%;
}

.agenda {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 768px;
  margin: 0 auto;
  min-height: 100vh;
}

.agenda__header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.agenda__logo {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  display: block;
}

.agenda__title {
  margin-top: 48px;
  margin-bottom: 72px;
  padding: 0 16px;
  font-weight: 700;
  color: var(--agenda-primary);
  font-size: 1.75rem;
}

.agenda__main {
  flex: 1;
  min-height: 0;
  width: 100%;
}

/* Event item */
.agenda-event {
  display: flex;
  align-items: stretch;
  gap: 10px;
  cursor: pointer;
  padding: 6px 0;
  width: 100%;
}

.agenda-event__bar {
  width: 5px;
  border-radius: 3px;
  flex-shrink: 0;
  background: var(--agenda-event-bar, #9e9e9e);
}

.agenda-event__content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  overflow: hidden;
}

.agenda-event__title {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agenda-event__desc {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  opacity: 0.85;
  font-size: 12px;
  overflow: hidden;
}

.agenda-event__desc-left {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.agenda-event__desc-right {
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 500;
}

/* Mobiscroll overrides */
.mbsc-ios .mbsc-selected .mbsc-calendar-cell-text,
.mbsc-ios .mbsc-calendar-day .mbsc-selected .mbsc-calendar-day-text {
  background: var(--agenda-primary) !important;
  border-color: var(--agenda-primary) !important;
  color: var(--agenda-on-primary) !important;
  border-radius: 6px;
}

.mbsc-ios .mbsc-button.mbsc-button-flat {
  color: var(--agenda-primary) !important;
}

.mbsc-calendar-wrapper {
  background: var(--agenda-panel-bg) !important;
}

/* force MUI + agenda area to use the agenda font */
.agenda, .agenda * ,
.MuiTypography-root,
.MuiButton-root,
.MuiChip-root {
  font-family: var(--font-family) !important;
}
      `}</style>

            {customCSS ? <style>{customCSS}</style> : null}

            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <Box className="agenda">
                    <Box className="agenda__header">
                        {logo.mediaURI && (
                            <Box className="agenda__logo-wrapper">
                                <img src={logo.mediaURI} alt="logo"
                                     className="agenda__logo"/>
                            </Box>
                        )}
                        {ui.locale['Title'] && (
                            <Typography variant="h4" className="agenda__title">
                                {ui.locale['Title']}
                            </Typography>
                        )}
                    </Box>

                    <Box className="agenda__main">
                        <Eventcalendar
                            data={events}
                            view={view}
                            locale={mobiscrollLocale}
                            theme="ios"
                            themeVariant="light"
                            selectedDate={selected}
                            onSelectedDateChange={(args) => {
                                if (args.date instanceof Date) setSelected(args.date);
                            }}
                            clickToCreate={false}
                            dragToCreate={false}
                            dragToMove={false}
                            dragToResize={false}
                            height="auto"
                            onEventClick={(args) => {
                                const event = args.event as ServiceEvent;
                                const startTime = new Date(event.start as string);
                                const isDisabled = startTime.getTime() < Date.now();
                                if (isDisabled) {
                                    setShowPastEventSnackbar(true);
                                    return;
                                }

                                setPreview(event);
                            }}
                            renderEvent={(mbscEvent) => (
                                <AgendaEventItem
                                    event={mbscEvent.original as ServiceEvent}
                                    onOpen={setPreview}
                                    locale={ui.locale}
                                />
                            )}
                        />
                    </Box>

                    <AgendaEventDialog
                        open={!!preview}
                        event={preview}
                        onClose={() => setPreview(undefined)}
                        defaultLanguageCode={defaultLanguageCode}
                        locale={ui.locale}
                        agenda={agenda}
                        refetch={() => refetch()}
                    />

                    <Footer/>
                </Box>

                <Snackbar
                    open={showPastEventSnackbar}
                    autoHideDuration={4000}
                    onClose={() => setShowPastEventSnackbar(false)}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                >
                    <Alert
                        onClose={() => setShowPastEventSnackbar(false)}
                        severity="info"
                        sx={{width: '100%'}}
                    >
                        {ui.locale['This event has already started'] ||
                            'This event has already started'}
                    </Alert>
                </Snackbar>
            </Box>
        </>
    );
};

export default AgendaView;